import Modal from 'react-modal';
import styled from 'styled-components';
import React, { FunctionComponent, useState, useEffect } from 'react';
import Papa from 'papaparse';
import _ from 'lodash';

import ToRGB from '../../utils/color';
import theme from '../../constants/theme';
import {
  ISavedOrder,
  useSaveOrderMutation,
  useGetShippingMethodsQuery,
} from '../../generated/graphql';
import toast from '../../utils/toast';

Modal.setAppElement('#root');

const { r, g, b } = ToRGB(theme.colors.blueKobelco);
const { r: rd, g: gd, b: bd } = ToRGB(theme.colors.blueDark);

const customStyles: Modal.Styles = {
  content: {
    top: undefined,
    left: undefined,
    right: undefined,
    bottom: undefined,
    border: 'none',
    borderRadius: 0,
    margin: '1rem',
    width: '100%',
    maxWidth: '520px',
    padding: '2.8rem 4rem',
    boxSizing: 'border-box',
    backgroundColor: theme.colors.white,
    boxShadow: `0px 4px 24px rgba(${rd}, ${gd}, ${bd}, 0.5)`,
  },
  overlay: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: `rgba(${r}, ${g}, ${b}, 0.8)`,
    zIndex: 11,
  },
};

const ModalTitle = styled.h1`
  font-size: 1.7rem;
  text-align: center;
  font-weight: normal;
  margin: 0 0 0.8rem 0;
  color: ${({ theme: { colors } }) => colors.blueDark};
  font-family: ${({ theme: { typography } }) => typography.museo};
`;

const ModalSubtitle = styled.h2`
  margin: 0;
  font-weight: 300;
  font-size: 1.2rem;
  text-align: center;
  font-weight: normal;
  color: ${({ theme: { colors } }) => colors.grayDark};
  font-family: ${({ theme: { typography } }) => typography.museo};
`;

const ActionsRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  margin-top: 1.4rem;
`;

const ActionButton = styled.button<{ cancel?: boolean }>`
  padding: 1rem;
  outline: none;
  cursor: pointer;
  min-width: 130px;
  font-family: ${({ theme: { typography } }) => typography.museo};
  color: ${({ theme: { colors }, cancel }) =>
    cancel ? colors.white : colors.blueNeutral};
  background-color: ${({ theme: { colors }, cancel }) =>
    cancel ? colors.blueKobelco : colors.white};
  border: 0.1rem solid
    ${({ theme: { colors }, cancel }) =>
      cancel ? colors.blueKobelco : colors.blueNeutral};
`;

const FileUpload = styled.input`
  width: 100%;
  padding: 1rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  margin-top: 1rem;
  font-family: ${({ theme: { typography } }) => typography.museo};
  font-size: 1rem;
  color: ${({ theme: { colors } }) => colors.blueDark};
  background-color: ${({ theme: { colors } }) => colors.white};
  cursor: pointer;
  &:hover {
    border-color: ${({ theme: { colors } }) => colors.blueKobelco};
  }
  &:focus {
    outline: none;
    border-color: ${({ theme: { colors } }) => colors.blueKobelco};
    box-shadow: 0 0 0.2rem rgba(${r}, ${g}, ${b}, 0.5);
  }
  &::file-selector-button {
    display: none;
  }
  &::before {
    content: 'Upload CSV';
    display: inline-block;
    padding: 0.5rem 1rem;
    background-color: ${({ theme: { colors } }) => colors.blueKobelco};
    color: ${({ theme: { colors } }) => colors.white};
    border-radius: 4px;
    font-family: ${({ theme: { typography } }) => typography.museo};
    font-size: 1rem;
    cursor: pointer;
    margin-right: 1rem;
    transition: background-color 0.3s ease;
  }
  &::before:hover {
    background-color: ${({ theme: { colors } }) => colors.blueDark};
  }
  &::before:active {
    background-color: ${({ theme: { colors } }) => colors.blueKobelco};
  }
  &::before:focus {
    outline: none;
    box-shadow: 0 0 0.2rem rgba(${r}, ${g}, ${b}, 0.5);
  }
`;

interface UploadCartModalProps {
  isOpen?: boolean;
  onCancel?: () => void;
  onConfirm?: () => void;
}

const UploadCartModal: FunctionComponent<UploadCartModalProps> = ({
  isOpen = false,
  onCancel = () => {},
  onConfirm = () => {},
}) => {
  const [modalIsOpen, setModalIsOpen] = useState(isOpen);
  const [file, setFile] = useState<null | unknown>(null);

  const [saveOrderMutation, { loading }] = useSaveOrderMutation({
    refetchQueries: ['searchSavedOrders'],
    onCompleted: () => {
      toast.show('Cart saved successfully!', 'success');
      onConfirm();
    },
    onError: error => {
      toast.show(
        'Error saving order. If you continue to see this error please contact support.',
        'error',
      );
      console.error('Error saving order:', error);
      onCancel();
    },
  });

  const methods = useGetShippingMethodsQuery();

  useEffect(() => {
    setModalIsOpen(isOpen);
  }, [isOpen]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const handleConfirm = () => {
    if (file) {
      Papa.parse(file as Papa.LocalFile, {
        header: true,
        complete: results => {
          const newSavedOrder = convertToSavedCart(results.data as CSVData);
          const shippingMethod = methods.data?.shippingMethods?.find(i => {
            return (
              i?.SOType === newSavedOrder.orderType &&
              i?.ShippingMethod === newSavedOrder.shippingMethod
            );
          });

          if (!shippingMethod) {
            toast.show(
              'Shipping method not found. Please make sure the shipping method is correct.',
              'error',
            );
            setFile(null);
            onCancel();
            return;
          }

          newSavedOrder.shippingMethodRaw = JSON.stringify(shippingMethod);

          saveOrderMutation({
            variables: {
              orderInput: _.omit(newSavedOrder, 'total'),
              total: newSavedOrder.total,
            },
          });
        },
        error: error => {
          toast.show(
            'Error parsing CSV file. Please make sure the file is in the correct format.',
            'error',
          );
          console.error('Error parsing CSV file:', error);
          setFile(null);
          onCancel();
        },
      });
    }
  };

  return (
    <Modal isOpen={modalIsOpen} style={customStyles} onRequestClose={onCancel}>
      <ModalTitle>Upload Cart CSV</ModalTitle>
      <ModalSubtitle>
        Upload your saved cart here and confirm to create a new saved cart
      </ModalSubtitle>
      <ActionsRow>
        <FileUpload type="file" accept=".csv" onChange={handleFileChange} />
      </ActionsRow>
      <ActionsRow>
        <ActionButton
          type="button"
          onClick={handleConfirm}
          style={{ marginRight: '1.9rem' }}
          disabled={!file || loading || methods.loading || !methods.data}
        >
          Submit
        </ActionButton>
        <ActionButton cancel type="button" onClick={onCancel}>
          Done
        </ActionButton>
      </ActionsRow>
    </Modal>
  );
};

type CSVData = Array<{
  Date?: string;
  Address?: string;
  Customer?: string;
  'Freight Code'?: string;
  'Order Type'?: string;
  'PO Number'?: string;
  'Shipping Method'?: string;
  'Shipping Method Raw'?: string;
  State?: string;
  Country?: string;
  Total?: number;
  Zip?: string;
  'Parts Number'?: string;
  'Parts Quantity'?: number;
}>;

function convertToSavedCart(data: CSVData) {
  const newOrder: Partial<ISavedOrder> = { parts: [] };

  for (const item of data) {
    if (
      item['Parts Number'] &&
      item['Parts Quantity'] &&
      Array.isArray(newOrder.parts)
    ) {
      newOrder.parts.push({
        number: item['Parts Number'],
        quantity: Number(item['Parts Quantity']),
      });
    } else if (
      item['Date'] &&
      item['Address'] &&
      item['Customer'] &&
      item['Freight Code'] &&
      item['Order Type'] &&
      item['PO Number'] &&
      item['Shipping Method'] &&
      item['State'] &&
      item['Country'] &&
      item['Total'] &&
      item['Zip']
    ) {
      console.log('item', item);
      let total = Number(item['Total']);
      // round to 2 decimal places
      total = Math.round(total * 100) / 100;

      newOrder.date = item['Date'];
      newOrder.address = item['Address'];
      newOrder.customer = item['Customer'];
      newOrder.freightCode = item['Freight Code'];
      newOrder.orderType = item['Order Type'];
      newOrder.poNumber = item['PO Number'];
      newOrder.shippingMethod = item['Shipping Method'];
      newOrder.state = item['State'];
      newOrder.country = item['Country'];
      newOrder.total = total;
      newOrder.zip = item['Zip'];
    }
  }

  return newOrder as ISavedOrder;
}

export default UploadCartModal;
